import { h, Component, Host, Prop, Watch, Element, State } from '@stencil/core';

import { getAbsoluteAssetPath } from '../../..';
import { BADGE_SHAPES, BADGE_FALLBACK } from '../../../services/interfaces';
import { isNumber } from '../../../services/utils';

@Component({
  tag: 'pk-badge',
  styleUrl: './scss/pk-badge.scss',
  assetsDirs: ['assets'],
  shadow: true,
})
export class PkBadge {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkBadgeElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() _fallbackImage: string = null;

  // Public Property API
  // ========================

  /** Alternative text description of the badge image */
  @Prop({ reflect: true }) alt!: string;

  /** Specifies extra information about the badge image,
   * this information is often shown as a tooltip text when the mouse moves over the adge image. */
  @Prop({ reflect: true }) badgeTitle?: string;

  /** Fallback image to use when there's no image yet or until it loads */
  @Prop({ reflect: true }) fallbackImage!: BADGE_FALLBACK;

  /** Set the size of the badge image */
  @Prop({ reflect: true, mutable: true }) size?: number;

  /** Set the shape of the badge image */
  @Prop({ reflect: true, mutable: true }) shape?: BADGE_SHAPES = BADGE_SHAPES.NONE;

  /** Url to load the bagde image from (this can be also a base64 encoded image or an inline SVG string) */
  @Prop({ reflect: true }) src?: string;

  // Prop lifecycle events
  // =======================

  @Watch('shape')
  handleShapeProp() {
    if (!Object.values(BADGE_SHAPES).includes(this.shape)) {
      console.warn(`[Pk-Badge]: When used, 'shape' property should be one of ${Object.values(BADGE_SHAPES)}`);
      this.shape = BADGE_SHAPES.NONE;
    }
  }

  @Watch('fallbackImage')
  handleFallbackImageProp() {
    if (Object.values(BADGE_FALLBACK).includes(this.fallbackImage)) {
      this._fallbackImage = getAbsoluteAssetPath(`./assets/${this.fallbackImage}` + '.svg');
    } else {
      if (this.fallbackImage) {
        console.warn(
          `[Pk-Badge]: When used, 'fallbackImage' property should be one of ${Object.values(BADGE_FALLBACK)}`,
        );
      }

      this._fallbackImage = null;
    }
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  connectedCallback() {
    this.setInitialSize();
    this.handleShapeProp();
    this.handleFallbackImageProp();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  // render() function
  // Always the last one in the class.
  // ===================================

  private setInitialSize = () => {
    /**
     * Setting the initial value in the prop will create an infinite loop
     * to avoid that we need to force the initial value when the compoonent gets connected
     * to the DOM, if no `size` property has been provided.
     */
    this.size = this.size && isNumber(this.size) ? this.size : null;
  };

  render() {
    const style = {
      ...(this.size && { '--badge--size': `${this.size}px` }),
    };

    return (
      <Host
        class={{
          'pk-badge': true,
          [`pk-badge--${this.shape}`]: Object.values(BADGE_SHAPES).includes(this.shape),
        }}
        style={{ ...style }}
      >
        <pk-image
          src={this.src}
          class="h-[var(--badge--size)] w-[var(--badge--size)]"
          fallbackImage={this._fallbackImage}
          alt={this.alt}
          imageTitle={this.badgeTitle}
          width={this.size}
          height={this.size}
          part="image"
        />
      </Host>
    );
  }
}
